import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { PlusCircle } from 'lucide-react';

function RecordPaymentDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('CASH');
  const [status, setStatus] = useState('PAID');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q.trim()) return setResults([]);
    const t = setTimeout(() => staffApi.get('/customers', { params: { q, pageSize: 8 } }).then(({ data }) => setResults(data.customers)), 250);
    return () => clearTimeout(t);
  }, [q]);

  const selectCustomer = async (c) => {
    setCustomer(c);
    const { data } = await staffApi.get(`/customers/${c.id}`);
    setSubscriptions(data.customer.subscriptions);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await staffApi.post('/payments', {
        customerId: customer.id,
        subscriptionId,
        amount: parseFloat(amount),
        method,
        status,
        referenceNumber: reference || undefined,
      });
      toast.success('Payment recorded');
      setOpen(false);
      setCustomer(null);
      setQ('');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="payment-record-open-btn" className="bg-saffron hover:bg-saffron-hover">
          <PlusCircle size={16} className="mr-2" /> Record Payment
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="payment-record-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>

        {!customer && (
          <div className="space-y-2">
            <Input data-testid="payment-customer-search" placeholder="Search customer…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-48 overflow-y-auto space-y-1">
              {results.map((c) => (
                <button key={c.id} data-testid="payment-customer-option" onClick={() => selectCustomer(c)} className="w-full text-left px-3 py-2 rounded-lg hover:bg-bone text-sm">
                  {c.name} · {c.phone}
                </button>
              ))}
            </div>
          </div>
        )}

        {customer && (
          <div className="space-y-3">
            <p className="text-sm font-medium">{customer.name}</p>
            <div className="space-y-1.5">
              <Label>Subscription</Label>
              <Select value={subscriptionId} onValueChange={setSubscriptionId}>
                <SelectTrigger data-testid="payment-subscription-select"><SelectValue placeholder="Select subscription" /></SelectTrigger>
                <SelectContent>
                  {subscriptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.subscriptionCode} — {s.plan?.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount (CAD)</Label>
                <Input type="number" data-testid="payment-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Method</Label>
                <Select value={method} onValueChange={setMethod}>
                  <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CASH">Cash</SelectItem>
                    <SelectItem value="INTERAC">Interac</SelectItem>
                    <SelectItem value="CARD">Card</SelectItem>
                    <SelectItem value="ONLINE">Online</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PAID">Paid</SelectItem>
                    <SelectItem value="PARTIAL">Partial</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="REFUNDED">Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Reference #</Label>
                <Input data-testid="payment-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button data-testid="payment-submit-btn" disabled={!customer || !subscriptionId || !amount || busy} onClick={submit} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Saving…' : 'Save Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Payments() {
  const [payments, setPayments] = useState([]);

  const load = () => staffApi.get('/payments').then(({ data }) => setPayments(data.payments));

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-2xl font-700 text-charcoal">Payments</h1>
        <RecordPaymentDialog onDone={load} />
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reference</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id} data-testid="payment-row">
                <TableCell>{new Date(p.paymentDate).toLocaleDateString('en-CA')}</TableCell>
                <TableCell>{p.subscription?.customer?.name}</TableCell>
                <TableCell>${Number(p.amount).toFixed(2)}</TableCell>
                <TableCell>{p.method}</TableCell>
                <TableCell>{p.status}</TableCell>
                <TableCell>{p.referenceNumber || '—'}</TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-ink py-8">No payments recorded yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
